import type {PaletteMode} from "@mui/material";

// Base palettes from the design system. The numeric keys intentionally mirror
// the names used in the design source (Gray 1, Brand 7, and so on).
export const grayScale = {
  1: "#FFFFFF",
  2: "#F2F2F5",
  3: "#EAEAED",
  4: "#DEDFE0",
  5: "#C3C3C7",
  6: "#8E8E92",
  7: "#57585C",
  8: "#424347",
  9: "#2F3033",
  10: "#252629",
  11: "#151517",
  12: "#1F2023",
  13: "#0E0F11",
} as const;

const grayOverlayBase = "#8B8BA3";
const darkLineOverlayBase = "#7F7F80";

export const brandScale = {
  1: "#EDF1FF",
  2: "#D5DFFF",
  3: "#ADC1FE",
  4: "#8EAAFD",
  5: "#6E92FB",
  6: "#5C85FF",
  7: "#3366FF",
  8: "#1641C2",
  9: "#11339A",
  10: "#122D7C",
  11: "#071643",
} as const;

export const greenScale = {
  1: "#E6FFF0",
  2: "#B3F5D0",
  3: "#82E8B1",
  4: "#4BDB93",
  5: "#25CF80",
  6: "#04C26F",
  7: "#009C5D",
  8: "#00754A",
  9: "#004F35",
  10: "#003727",
  11: "#022A12",
} as const;

export const redScale = {
  1: "#FFF0F0",
  2: "#FFDBDC",
  3: "#FFB3B8",
  4: "#FF8A95",
  5: "#FF6176",
  6: "#FF3859",
  7: "#D92549",
  8: "#B3153A",
  9: "#8C0A2D",
  10: "#660623",
  11: "#450A1C",
} as const;

export const orangeScale = {
  1: "#FFF7ED",
  2: "#FFEDD5",
  3: "#FED7AA",
  4: "#FDBA74",
  5: "#FB923C",
  6: "#FF6F23",
  7: "#EA580C",
  8: "#C2410C",
  9: "#9A3412",
  10: "#7C2D12",
  11: "#431407",
} as const;

const withOpacity = (hex: string, opacity: number): string => {
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();
  return `${hex}${alpha}`;
};

export interface SemanticColorTokens {
  static: {black: string; white: string};
  link: {normal: string; click: string; disabled: string};
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    disabled: string;
    inversePrimary: string;
    inverseSecondary: string;
    inverseTertiary: string;
    inverseDisabled: string;
    mask: string;
    activeDisable: string;
    tooltip: string;
    brand: string;
    orange: string;
  };
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    disabled: string;
    inversePrimary: string;
    inverseSecondary: string;
    inverseTertiary: string;
    inverseDisabled: string;
    error: string;
    success: string;
    warning: string;
  };
  line: {
    gutter: string;
    subtle: string;
    strong: string;
    input: string;
    brand: string;
  };
  function: {
    primary: string;
    primaryClick: string;
    primaryDisabled: string;
    green: string;
    greenClick: string;
    greenDisabled: string;
    red: string;
    redClick: string;
    redDisabled: string;
    orange: string;
    orangeClick: string;
    orangeDisabled: string;
  };
  layer: {
    modal: string;
    card: string;
    background: string;
    dropdown: string;
    active: string;
    brandActive: string;
  };
}

const sharedFeedbackTokens = {
  green: greenScale[6],
  greenClick: greenScale[7],
  red: redScale[6],
  redClick: redScale[7],
  orange: orangeScale[6],
  orangeClick: orangeScale[7],
} as const;

export const getSemanticColorTokens = (
  mode: PaletteMode,
): SemanticColorTokens => {
  const isLight = mode === "light";

  return {
    static: {black: grayScale[11], white: grayScale[1]},
    link: isLight
      ? {
          normal: brandScale[6],
          click: brandScale[8],
          disabled: brandScale[2],
        }
      : {
          normal: brandScale[5],
          click: brandScale[7],
          disabled: brandScale[8],
        },
    background: isLight
      ? {
          primary: grayScale[1],
          secondary: grayScale[2],
          tertiary: grayScale[4],
          disabled: grayScale[3],
          inversePrimary: grayScale[11],
          inverseSecondary: grayScale[10],
          inverseTertiary: grayScale[8],
          inverseDisabled: grayScale[9],
          mask: withOpacity(grayScale[11], 0.7),
          activeDisable: withOpacity(grayOverlayBase, 0.12),
          tooltip: grayScale[8],
          brand: withOpacity(brandScale[7], 0.1),
          orange: orangeScale[1],
        }
      : {
          primary: grayScale[11],
          secondary: withOpacity(grayOverlayBase, 0.1),
          tertiary: grayScale[8],
          disabled: grayScale[9],
          inversePrimary: grayScale[1],
          inverseSecondary: grayScale[2],
          inverseTertiary: grayScale[4],
          inverseDisabled: grayScale[3],
          mask: withOpacity(grayScale[11], 0.5),
          activeDisable: withOpacity(grayOverlayBase, 0.12),
          tooltip: grayScale[8],
          brand: withOpacity(brandScale[7], 0.2),
          orange: withOpacity(orangeScale[6], 0.1),
        },
    text: isLight
      ? {
          primary: grayScale[11],
          secondary: grayScale[7],
          tertiary: grayScale[6],
          disabled: grayScale[5],
          inversePrimary: grayScale[2],
          inverseSecondary: grayScale[5],
          inverseTertiary: grayScale[6],
          inverseDisabled: grayScale[5],
          error: redScale[6],
          success: greenScale[6],
          warning: orangeScale[6],
        }
      : {
          primary: grayScale[2],
          secondary: grayScale[3],
          tertiary: grayScale[6],
          disabled: grayScale[7],
          inversePrimary: grayScale[11],
          inverseSecondary: grayScale[7],
          inverseTertiary: grayScale[6],
          inverseDisabled: grayScale[7],
          error: redScale[6],
          success: greenScale[6],
          warning: orangeScale[6],
        },
    line: isLight
      ? {
          gutter: grayScale[2],
          subtle: grayScale[3],
          strong: grayScale[4],
          input: brandScale[7],
          brand: withOpacity(brandScale[2], 0.6),
        }
      : {
          gutter: withOpacity(darkLineOverlayBase, 0.12),
          subtle: withOpacity(darkLineOverlayBase, 0.16),
          strong: withOpacity(darkLineOverlayBase, 0.3),
          input: brandScale[7],
          brand: withOpacity(brandScale[2], 0.12),
        },
    function: isLight
      ? {
          primary: brandScale[7],
          primaryClick: brandScale[8],
          primaryDisabled: brandScale[3],
          ...sharedFeedbackTokens,
          greenDisabled: greenScale[3],
          redDisabled: redScale[3],
          orangeDisabled: orangeScale[3],
        }
      : {
          primary: brandScale[6],
          primaryClick: brandScale[7],
          primaryDisabled: brandScale[9],
          ...sharedFeedbackTokens,
          greenDisabled: greenScale[8],
          redDisabled: redScale[8],
          orangeDisabled: orangeScale[8],
        },
    layer: isLight
      ? {
          modal: grayScale[1],
          card: grayScale[1],
          background: grayScale[3],
          dropdown: grayScale[1],
          active: grayScale[1],
          brandActive: withOpacity(brandScale[2], 0.2),
        }
      : {
          modal: grayScale[10],
          card: withOpacity(grayOverlayBase, 0.1),
          background: grayScale[9],
          dropdown: grayScale[8],
          active: grayScale[8],
          brandActive: withOpacity(brandScale[2], 0.08),
        },
  };
};

// Compatibility aliases for existing components. New code should prefer
// theme.palette.tokens so semantic colors switch with light/dark mode.
export const grey = {
  50: grayScale[1],
  100: grayScale[2],
  200: grayScale[3],
  300: grayScale[4],
  400: grayScale[5],
  450: grayScale[6],
  500: grayScale[7],
  600: grayScale[8],
  700: grayScale[9],
  800: grayScale[10],
  900: grayScale[11],
} as const;

export const primary = {
  50: brandScale[1],
  100: brandScale[2],
  200: brandScale[3],
  300: brandScale[4],
  400: brandScale[5],
  450: brandScale[6],
  500: brandScale[7],
  600: brandScale[8],
  700: brandScale[9],
  800: brandScale[10],
  900: brandScale[11],
} as const;

export const aptosColor = brandScale[7];
export const negativeColor = redScale[6];
export const warningColor = orangeScale[6];

export const codeBlockColor = withOpacity(brandScale[7], 0.1);
export const codeBlockColorClickableOnHover = withOpacity(brandScale[7], 0.2);
export const codeBlockColorRgbLight = brandScale[1];
export const codeBlockColorRgbDark = brandScale[11];

export const textColorLight = brandScale[8];
export const textColorDark = brandScale[4];
